import { ApiResponse } from 'apisauce';
import * as _ from 'lodash';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { HealAPI } from '../../healApi';
import { toast } from '../message/actions';
import { ServerResponse } from '../types';
import { approvePatientFile, deletePatientFile, FetchFilesAction, GenericPatientFilesAction, setFiles, setFilesFetching, updatePatientFile } from './actions';
import { PatchFileRequest, PatientFileData, PatientFileResponse } from './types';

function* handlePatientFilesFetchFiles(action: FetchFilesAction) {
  yield put(setFilesFetching(action.data.patientId, true));
  yield call(fetchPatientFiles, action.data.patientId);
}

function* fetchPatientFiles(patientId: string, page = 1): any {
  const [unreleasedFiles, releasedFiles] = yield all([call(HealAPI.fetchPatientFiles, patientId, 'unreleased', page), call(HealAPI.fetchPatientFiles, patientId, 'files', page)]);

  let files: any = [];

  if (unreleasedFiles.data && unreleasedFiles.data._items) {
    files = files.concat(unreleasedFiles.data._items);
  }

  if (releasedFiles.data && releasedFiles.data._items) {
    files = files.concat(releasedFiles.data._items);
  }

  files = _.sortBy(files, '_created').reverse();

  yield put(setFiles(patientId, files));
  yield put(setFilesFetching(patientId, false, !unreleasedFiles.ok || !releasedFiles.ok ? "Couldn't retrieve all files for this patient, please refresh the page." : undefined));
}

function* handlePatientFilesActions(action: GenericPatientFilesAction) {
  const { files, patientId } = action.data;
  let toastActionName = '';
  let responses: ApiResponse<ServerResponse>[] = [];

  yield put(setFilesFetching(action.data.patientId, true));

  switch (action.type) {
    case 'PATIENT_FILES_START_SENDING_FILES':
      responses = yield all(files.map((file) => call(sendFileToDrChrono, patientId, file)));
      toastActionName = 'sent to DrChrono';
      break;
    case 'PATIENT_FILES_START_UPDATING_FILES':
      responses = yield all(files.map((file) => call(updateFile, patientId, file)));
      toastActionName = 'updated';
      break;
    case 'PATIENT_FILES_START_RELEASING_FILES':
      responses = yield all(files.filter((f) => f.context === 'unreleased').map((file) => call(releaseFile, patientId, file)));
      toastActionName = 'sent to the patient';
      break;
    case 'PATIENT_FILES_START_DELETING_FILES':
      responses = yield all(files.map((file) => call(deleteFile, patientId, file)));
      toastActionName = 'deleted';
      break;
  }

  if (responses.filter((r) => !r.ok).length > 0) {
    yield put(
      toast({
        type: 'error',
        title: 'Error',
        content: 'An error occurred, not all selected files have been ' + toastActionName + ', please try again',
      })
    );
  } else {
    if (action.type === 'PATIENT_FILES_START_RELEASING_FILES') {
      yield call(fetchPatientFiles, patientId);
    }
    yield put(
      toast({
        type: 'success',
        title: 'Success',
        content: 'Files have been ' + toastActionName,
      })
    );
  }

  yield put(setFilesFetching(action.data.patientId, false));
}

function* sendFileToDrChrono(patientId: string, file: PatientFileData) {
  const response: ApiResponse<PatientFileResponse> = yield call(HealAPI.approvePatientFile, patientId, file._id, file.context, file.description);
  if (response && response.ok && response.data) {
    yield put(approvePatientFile(patientId, response.data.data));
  }
  return response;
}

function* deleteFile(patientId: string, file: PatientFileData) {
  yield call(HealAPI.deletePatientFile, patientId, file._id, file.context);
  yield put(deletePatientFile(patientId, file));
  return { ok: true };
}

function* releaseFile(patientId: string, file: PatientFileData): any {
  return yield call(HealAPI.releasePatientFile, patientId, file._id, file.visit_code, file.document_type, true);
}

function* updateFile(patientId: string, file: PatientFileData) {
  const data: PatchFileRequest = {
    document_type: file.document_type,
    visit_code: file.visit_code,
    description: file.description,
    released_at: file.released_at,
  };
  const response: ApiResponse<ServerResponse> = yield call(HealAPI.patchFile, patientId, file._id, data, file.context);
  if (response && response.ok) {
    yield put(updatePatientFile(patientId, file));
  }
  return response;
}

export function* patientFilesFlow() {
  yield takeEvery(['PATIENT_FILES_FETCH'], handlePatientFilesFetchFiles);
  yield takeEvery(
    ['PATIENT_FILES_START_UPDATING_FILES', 'PATIENT_FILES_START_SENDING_FILES', 'PATIENT_FILES_START_RELEASING_FILES', 'PATIENT_FILES_START_DELETING_FILES'],
    handlePatientFilesActions
  );
}
